// Запросы метрик и импорта: /api/metrics/*, /api/import/*, /api/sources.
// Общие для раздела «Здоровье» (вес, замеры) и экрана «Показатели».

import { useQuery } from '@tanstack/react-query'

import { api } from '@/api/client'

export type MetricCategory = 'body' | 'measurement' | 'vitals' | 'activity' | 'recovery' | 'nutrition'

export type MetricType = {
  key: string
  label_ru: string
  unit: string
  category: MetricCategory | string
  min_value: number | null
  max_value: number | null
  is_custom: boolean
}

export type MetricSample = {
  id: string
  metric: string
  measured_at: string
  day: string
  value: number
  source: string
  source_ref: string | null
  note: string | null
}

export type Bucket = 'raw' | 'day' | 'week'

export type Point = { day: string; at: string | null; value: number; avg: number | null }

export type Series = {
  metric: MetricType
  bucket: Bucket
  points: Point[]
  latest: Point | null
  trend: number | null
}

export type SamplePage = { items: MetricSample[]; next_cursor: string | null }

export type RowError = { row: number; message: string; field: string | null }

export type ImportResult = {
  import_run_id: string
  source: string
  filename: string | null
  rows_ok: number
  rows_failed: number
  rows_skipped: number
  written: number
  errors: RowError[]
}

export type DateFormat = 'auto' | 'iso' | 'dmy' | 'mdy' | 'unix' | 'unix_ms'

export type CsvMapping = {
  date_col: string
  date_format: DateFormat
  weight_col: string
  body_fat_col: string | null
}

export type CsvPreview = {
  filename: string | null
  columns: string[]
  rows: string[][]
  total_rows: number
  suggested: CsvMapping | null
}

export type ZeppImport =
  | { status: 'done'; result: ImportResult; preview: null }
  | { status: 'needs_mapping'; result: null; preview: CsvPreview }

export type SampleInput = { metric: string; value: number; measured_at: string | null; note: string | null }
export type SamplePatch = { value?: number; measured_at?: string; note?: string | null }

export type SeriesQuery = {
  start: string | null
  end?: string | null
  bucket?: Bucket | 'auto'
  trend?: number | null
}

export const metricKeys = {
  all: ['metrics'] as const,
  types: ['metrics', 'types'] as const,
  series: (metric: string, q: SeriesQuery) => ['metrics', 'series', metric, q] as const,
  samples: (metric: string | null) => ['metrics', 'samples', metric] as const,
}

function fileForm(file: File, fields: Record<string, string | null | undefined> = {}): FormData {
  const form = new FormData()
  form.append('file', file)
  for (const [key, value] of Object.entries(fields)) {
    if (value !== null && value !== undefined && value !== '') form.append(key, value)
  }
  return form
}

export const metricsApi = {
  types: () => api.get<MetricType[]>('/metrics/types'),
  addCustom: (label_ru: string, unit: string) => api.post<MetricType>('/metrics/types', { label_ru, unit }),
  series: (metric: string, q: SeriesQuery) =>
    api.get<Series>(`/metrics/${encodeURIComponent(metric)}`, {
      query: { start: q.start, end: q.end, bucket: q.bucket ?? 'auto', trend: q.trend ?? undefined },
    }),
  samples: (metric: string | null, cursor: string | null, limit = 30) =>
    api.get<SamplePage>('/metrics/samples', { query: { metric, cursor, limit } }),
  add: (input: SampleInput) => api.post<MetricSample>('/metrics/samples', input),
  addBatch: (values: Record<string, number>, measured_at: string | null, note: string | null) =>
    api.post<MetricSample[]>('/metrics/samples/batch', { values, measured_at, note }),
  update: (id: string, patch: SamplePatch) => api.patch<MetricSample>(`/metrics/samples/${id}`, patch),
  remove: (id: string) => api.delete<{ change_id: string }>(`/metrics/samples/${id}`),
  undo: (changeId: string) => api.post(`/changes/${changeId}/undo`),
  importZepp: (file: File, password: string | null) =>
    api.post<ZeppImport>('/import/zepp', fileForm(file, { password })),
  previewCsv: (file: File) => api.post<CsvPreview>('/import/csv/preview', fileForm(file)),
  commitCsv: (file: File, mapping: CsvMapping) =>
    api.post<ImportResult>(
      '/import/csv/commit',
      fileForm(file, {
        date_col: mapping.date_col,
        date_format: mapping.date_format,
        weight_col: mapping.weight_col,
        body_fat_col: mapping.body_fat_col,
      }),
    ),
}

export function useMetricTypes() {
  return useQuery({ queryKey: metricKeys.types, queryFn: metricsApi.types, staleTime: 5 * 60_000 })
}

export function useSeries(metric: string | null, q: SeriesQuery) {
  return useQuery({
    queryKey: metricKeys.series(metric ?? '', q),
    queryFn: () => metricsApi.series(metric ?? '', q),
    enabled: Boolean(metric),
  })
}
